/**
 * @file tooltip.tsx
 *
 * @changelog
 * v1.2.0
 * - Added shadow to content
 * - Added border to arrow
 * - Removed close animations from content, tooltip flickered when parents unmount
 * - Removed open animations from content, arrow flickered when opening
 * v1.1.0
 * - Add generic to Tooltip for type-safe detachable triggers
 * - Added aria-label as prop to Trigger
 * v1.0.0
 * - Added with shadcn CLI
 * - Swapped `cva` for `tv`
 */
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { cn } from 'tailwind-variants'

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  )
}

function Tooltip<Payload = unknown>({
  ...props
}: TooltipPrimitive.Root.Props<Payload>) {
  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      {...props}
    />
  )
}

function TooltipTrigger({
  ...props
}: TooltipPrimitive.Trigger.Props & { ['aria-label']: string }) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...props}
    />
  )
}

function TooltipContent({
  className,
  side = 'top',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  >) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'z-50 inline-flex w-fit max-w-xs origin-(--transform-origin) items-center gap-1.5 rounded-md bg-popover border border-border px-3 py-1.5 text-xs text-foreground has-data-[slot=kbd]:pr-1.5 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95',
            'shadow-lg',
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow
            className={cn(
              'z-40 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-xs bg-popover',
              'data-[side=bottom]:top-1',
              'data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2',
              'data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2',
              'data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2',
              'data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2',
              'data-[side=top]:-bottom-2.5',
              // Arrow borders
              'data-[side=top]:drop-shadow-[1px_1px_0_var(--popover-border)]',
              'data-[side=right]:drop-shadow-[-1px_1px_0_var(--popover-border)]',
              'data-[side=bottom]:drop-shadow-[-1px_-1px_0_var(--popover-border)]',
              'data-[side=left]:drop-shadow-[1px_-1px_0_var(--popover-border)]',
              // Arrow position adjustments
              'data-[side=left]:mr-px',
              'data-[side=right]:ml-px',
            )}
          />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
